/**
 * BrowserRunScreenshotDialog — modal that shows the failure screenshot for a single flow.
 * Session 271 (Feb 2026). Loaded via authenticated fetch → blob URL because the
 * screenshot endpoint is admin-gated and cannot ride a bare `<img src>` request.
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertTriangle } from 'lucide-react';
import { getToken } from '@/utils/tokenManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BrowserRunScreenshotDialog = ({ flowId, error, open, onClose }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!open || !flowId) return undefined;
    let cancelled = false;
    let currentUrl = null;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await fetch(`${API_URL}/api/admin/code-health/golden-paths/browser-run/screenshot/${flowId}`,
          { headers: { Authorization: `Bearer ${getToken()}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        currentUrl = URL.createObjectURL(blob);
        setBlobUrl(currentUrl);
      } catch (e) {
        if (!cancelled) setLoadError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      setBlobUrl(null);
    };
  }, [open, flowId]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl bg-gray-900 border-gray-800 text-gray-100" data-testid="br-screenshot-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            Failure screenshot — <span className="font-mono text-red-300">{flowId}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-400 break-all">{error || 'No error message recorded.'}</DialogDescription>
        </DialogHeader>
        <div className="mt-2 min-h-[200px] flex items-center justify-center">
          {loading && <p className="text-xs text-gray-400">Loading screenshot…</p>}
          {loadError && <p className="text-xs text-red-300" data-testid="br-screenshot-error">Could not load screenshot — most likely it was auto-deleted after your first view. Re-run the pipeline if you need to see it again.</p>}
          {blobUrl && !loadError && (
            <img
              src={blobUrl}
              alt={`Failure screenshot for ${flowId}`}
              className="max-w-full max-h-[70vh] rounded border border-gray-800"
              data-testid="br-screenshot-image"
            />
          )}
        </div>
        <p className="text-[11px] text-gray-500 mt-2" data-testid="br-screenshot-privacy-note">
          Privacy-First: this picture was captured with Privacy Mode on, so balances, holding names and the Code Name are masked. The picture is auto-deleted from the server the instant you view it.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default BrowserRunScreenshotDialog;
